import { VizConfigBuilder } from "@/shared/config/VizConfig";
import { LayoutRecord } from "@/shared/domain/layouts";
import { LayoutDefinition } from "@/shared/domain/layouts/LayoutDefinition";
import { Requirement, RuleStyle } from "@/shared/domain/layouts/LayoutRecord";
import { TopicNode } from "@/shared/domain/topics";
import { LoggerService } from "@/shared/services";
import { State, TopicData } from "@/shared/state/visualization";
import { Layout, PanelStates } from "@/shared/state/visualization/schema/v1";

import { layoutRuleMatcher, MatchFailure } from "./LayoutRuleMatcher";

type MatchedData = { data: TopicData[]; style: RuleStyle };
type Bindings = Record<string, MatchedData[]>;

type MatchFailureReason = {
  type: MatchFailure;
  message: string;
};

export interface MatchError {
  ruleId: string;
  requirement: Requirement;
  reason: MatchFailureReason;
}

interface MatchResult {
  bindings: Bindings;
  errors: Record<string, MatchError[]>; // panelId -> errors
}

export interface LayoutToVizConfigContext {
  topicTrees: TopicNode[];
}

/**
 * Resolves a provided {@link LayoutRecord} into a {@link State} object while returning
 * any {@link MatchError} that occurred during the transformation.
 *
 * @throws {InvalidLayoutDefinitionError} if the layout is invalid
 * @throws {UnsupportedLayoutSchemaVersion} if the layout schema version is unsupported
 */
export function layoutToVizConfig(
  layout: LayoutRecord,
  context: LayoutToVizConfigContext,
): {
  panels: { [id: string]: PanelStates };
  layout: State["layout"];
  errors: Record<string, MatchError[]>;
} {
  LoggerService.log("Creating viz state from layout:", layout);

  // Clamp all relativeSize values to [0, 1] BEFORE validation
  const clampedLayout = clampRelativeSizes(layout.layout_definition.layout);

  // Validate layout definition and throw an error if invalid
  const layoutDefinition = LayoutDefinition.from_obj(
    {
      ...layout.layout_definition,
      layout: clampedLayout,
    },
    layout.schema_version,
  );

  const { bindings, errors } = matchLayoutRules(
    layoutDefinition,
    context.topicTrees,
  );

  const vizConfigBuilder = new VizConfigBuilder().setLayout(clampedLayout);

  layoutDefinition.panels.forEach(({ rules, ...panel }) => {
    // Map the panel's rules to bound data
    const matchedData = rules.map((ruleId) => bindings[ruleId] ?? []).flat();

    // Massage the data into the format expected by the panel
    const panelData = VizConfigBuilder.transformTopicDataForPanel(
      panel.type,
      panel.id,
      matchedData,
    );

    // Insert the panel and its data into VizConfig
    vizConfigBuilder.addPanel({
      ...panel,
      data: panelData,
    } as PanelStates);
  });

  const vizConfig = vizConfigBuilder.build();

  return {
    panels: vizConfig.panels,
    layout: vizConfig.layout,
    errors,
  };
}

/**
 * Recursively clamps all relativeSize values in a layout tree to be between 0 and 1.
 * This is a defensive measure to handle layouts that may have been saved with invalid
 * relativeSize values due to bugs.
 *
 * @param layout - The layout tree to clamp
 * @returns A new layout tree with all relativeSize values clamped to [0, 1]
 */
function clampRelativeSizes(layout: Layout): Layout {
  // Helper to clamp a single relative size value and log if needed
  const clampSize = (size: number, id: string, itemType: string): number => {
    const clamped = Math.max(0, Math.min(1, size));
    if (size !== clamped) {
      LoggerService.warn(
        `Clamped ${itemType} relativeSize from ${size} to ${clamped} for ${id}`,
      );
    }
    return clamped;
  };

  const clampedChildren = layout.children.map((child) => {
    if ("children" in child) {
      // Recursively clamp nested layouts
      return clampRelativeSizes(child);
    } else {
      // Clamp layout items
      return {
        ...child,
        relativeSize: clampSize(child.relativeSize, child.id, "layout item"),
      };
    }
  });

  return {
    ...layout,
    relativeSize: clampSize(layout.relativeSize, layout.id, "layout"),
    children: clampedChildren,
  };
}

/**
 * Matches layout rules against available topics and returns the bindings
 * and any match errors that occurred.
 *
 * Uses the {@link layoutRuleMatcher} to find matches for each rule.
 */
function matchLayoutRules(
  layoutDefinition: LayoutDefinition,
  topicTrees: TopicNode[],
): MatchResult {
  const bindings: Bindings = {};
  const errors: Record<string, MatchError[]> = {};

  layoutDefinition.rules.forEach((rule) => {
    const { matches, failures } = layoutRuleMatcher.findMatches(
      rule,
      topicTrees,
    );

    if (matches.length > 0) {
      LoggerService.log(
        `Matched rule ${rule.id} with ${matches.length} matches`,
      );
      bindings[rule.id] = matches.map((match) => ({
        data: match.topicData,
        style: rule.style ?? {},
      }));
    }

    if (failures.length > 0) {
      LoggerService.warn(
        `Failed to match rule ${rule.id} with ${failures.length} failures`,
        failures,
      );

      // Find panels that use this rule and report it as an error per panel
      layoutDefinition.panels.forEach((panel) => {
        if (panel.rules.includes(rule.id)) {
          errors[panel.id] = errors[panel.id] || [];
          errors[panel.id].push(
            ...failures.map((failure) => ({
              ruleId: failure.rule.id,
              requirement: failure.requirement,
              reason: failure.reason,
            })),
          );
        }
      });
    }
  });

  return { bindings, errors };
}
